import type { ReactNode } from "react";

import { Tag } from "../Tag";

import { boundedItems } from "./boundedItems";
import styles from "./ConfirmationCard.module.css";

/**
 * Default number of chips rendered before the rest collapse to a count.
 *
 * The lists come from model output and are unbounded; past a couple of dozen
 * entries a reviewer is scrolling rather than reviewing, so the tail is worth
 * less than the count of it.
 */
const DEFAULT_CHIP_LIMIT = 25;

export interface ConfirmationPanelProps {
  children: ReactNode;
  /** Accessible name for the preview group. */
  label?: string;
}

/**
 * Shell around a tool-approval preview.
 *
 * Deliberately *not* a scroll region, and deliberately not height-bounded. The
 * host decides where the overflow lands, because only the host knows what else
 * shares the box: the inline card scrolls its preview slot and keeps
 * Approve/Decline outside it, and the details modal scrolls its content and
 * keeps the title and actions pinned. When this panel bounded itself too, a
 * long procedure produced a scrollbar here *and* one on the host — the reader
 * had to work out which of them to drag.
 */
export function ConfirmationPanel({ children, label }: ConfirmationPanelProps) {
  return (
    <div
      className={styles.panel}
      role="group"
      aria-label={label ?? "Tool request preview"}
    >
      {children}
    </div>
  );
}

export interface ConfirmationCardProps {
  /** Prominent heading — usually the name of the thing being created or changed. */
  title?: ReactNode;
  /** Short chip rendered opposite the title (e.g. a resource type). */
  badge?: ReactNode;
  children?: ReactNode;
}

/**
 * Bordered card holding one proposed change.
 */
export function ConfirmationCard({
  title,
  badge,
  children,
}: ConfirmationCardProps) {
  return (
    <div className={styles.card}>
      {(title !== undefined || badge !== undefined) && (
        <div className={styles.header}>
          {title !== undefined && <div className={styles.title}>{title}</div>}
          {badge !== undefined && <span className={styles.badge}>{badge}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

export interface ConfirmationFieldsProps {
  children: ReactNode;
}

/**
 * Definition list wrapping {@link ConfirmationField} pairs.
 *
 * A `<dl>` rather than sibling `<span>`s so a screen-reader user hears each
 * value tied to its label instead of an undifferentiated stream.
 */
export function ConfirmationFields({ children }: ConfirmationFieldsProps) {
  return <dl className={styles.fields}>{children}</dl>;
}

export interface ConfirmationFieldProps {
  label: ReactNode;
  children: ReactNode;
}

/** One label/value pair. Must be rendered inside {@link ConfirmationFields}. */
export function ConfirmationField({ label, children }: ConfirmationFieldProps) {
  return (
    <div>
      <dt className={styles.fieldLabel}>{label}</dt>
      <dd className={styles.fieldValue}>{children}</dd>
    </div>
  );
}

export interface ConfirmationChipsProps {
  /** Values to render, in proposal order. Duplicates collapse. */
  items: readonly string[];
  /** How many to render before the remainder collapses to a count. */
  max?: number;
}

/**
 * Bounded, deduped list of short monospace values (typically resource IDs).
 *
 * A `<ul>` so the entries are announced as a list with a count rather than as
 * a run of loose text.
 */
export function ConfirmationChips({
  items,
  max = DEFAULT_CHIP_LIMIT,
}: ConfirmationChipsProps) {
  const { shown, hiddenCount } = boundedItems(items, max);
  return (
    <ul className={styles.chips}>
      {shown.map((item) => (
        <li key={item} className={styles.chip}>
          {item}
        </li>
      ))}
      {hiddenCount > 0 && (
        <li className={styles.overflow}>and {hiddenCount} more</li>
      )}
    </ul>
  );
}

export interface ConfirmationTagsProps {
  tags: readonly string[];
  max?: number;
}

/** Bounded, deduped list of tags, rendered with the {@link Tag} primitive. */
export function ConfirmationTags({
  tags,
  max = DEFAULT_CHIP_LIMIT,
}: ConfirmationTagsProps) {
  const { shown, hiddenCount } = boundedItems(tags, max);
  return (
    <ul className={styles.chips}>
      {shown.map((tag) => (
        <li key={tag}>
          <Tag label={tag} />
        </li>
      ))}
      {hiddenCount > 0 && (
        <li className={styles.overflow}>and {hiddenCount} more</li>
      )}
    </ul>
  );
}

export interface ConfirmationBulletsProps {
  children: ReactNode;
}

/** Plain bulleted list for free-form summary lines. */
export function ConfirmationBullets({ children }: ConfirmationBulletsProps) {
  return <ul className={styles.bullets}>{children}</ul>;
}

export interface ConfirmationTextProps {
  children: ReactNode;
}

/**
 * A statement that the proposed request cannot succeed.
 *
 * `role="alert"` and a visually distinct treatment, because this is not
 * another data field: approving anyway produces a failed write.
 */
export function ConfirmationAlert({ children }: ConfirmationTextProps) {
  return (
    <p className={styles.alert} role="alert">
      {children}
    </p>
  );
}

/** Secondary aside — degraded display, caveats, post-approval behaviour. */
export function ConfirmationNote({ children }: ConfirmationTextProps) {
  return <p className={styles.note}>{children}</p>;
}

/** Introductory line above the card. */
export function ConfirmationLead({ children }: ConfirmationTextProps) {
  return <div className={styles.lead}>{children}</div>;
}

/** "Nothing to preview" state. */
export function ConfirmationEmpty({ children }: ConfirmationTextProps) {
  return <div className={styles.empty}>{children}</div>;
}
